import axios from "axios";

/* ================= BASE URL ================= */
// Relative path — Vercel rewrites /api/* to the Render backend (see
// client/vercel.json). Keeping this same-origin (rather than the old
// full onrender.com URL) is what makes the httpOnly session cookie
// actually work in Safari/Firefox, which block cross-site cookies.
const API = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

/* ================= TOKEN ================= */
// The real session now lives in an httpOnly cookie the server sets on
// login/signup — JS (and therefore an XSS bug) can never read it.
// This header is kept only as a harmless fallback for any tab still
// running an older build with a token cached in localStorage.
API.interceptors.request.use((req) => {
  const token = localStorage.getItem("token");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

/* ================= AUTH ================= */
export const signup    = (data) => API.post("/auth/signup", data);
export const verifyOtp = (data) => API.post("/auth/verify-otp", data);
export const resendOtp = (data) => API.post("/auth/resend-otp", data);

export const login = async (data) => {
  const res = await API.post("/auth/login", data);
  // Real session = httpOnly cookie, already set by the server response.
  // This flag is just non-sensitive UI state ("am I logged in?") — safe
  // even if an XSS bug reads it, since it can't authenticate anything.
  if (res.data.token) localStorage.setItem("subai_session", "1");
  return res;
};

export const getProfile = () => API.get("/profile");

/* ================= FILE ================= */
export const uploadFile   = (formData) =>
  API.post("/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const getMyUploads = () => API.get("/my-uploads");
export const deleteFile   = (id) => API.delete(`/delete/${id}`);

/* ================= SUBTITLE ================= */
export const getSubtitle  = (id) => API.get(`/subtitle/${id}`);
export const saveSubtitle = (id, subtitles) =>
  API.post(`/subtitle/save/${id}`, { subtitles });

/* ================= CONVERT ================= */
// provider: "gemini" ya "openai"
// batchSize: 1-80 (bada = fast + RPM safe + quota bachta)
export const convertSubtitle = async (
  id,
  apiKey,
  provider = "gemini",
  batchSize = 30
) => {
  try {
    const res = await API.post(`/convert/${id}`, {
      apiKey,
      provider,
      batchSize,
    });
    return res;
  } catch (err) {
    const msg = (err.response?.data?.message || "").toLowerCase();

    if (msg.includes("rpm") || msg.includes("rate") || msg.includes("429"))
      throw new Error("RPM limit hit ho gayi! Batch size badhao ya 1 min baad try karo.");

    if (msg.includes("quota") || msg.includes("250"))
      throw new Error("API quota khatam ho gayi. Kal dobara try karo.");

    if (msg.includes("api key") || msg.includes("invalid"))
      throw new Error("API key galat hai. Check karo.");

    if (msg.includes("nahi mili") || msg.includes("not found"))
      throw new Error("File nahi mili. Dobara upload karo.");

    throw new Error(err.response?.data?.message || "Convert failed. Dobara try karo.");
  }
};

/* ================= BUG REPORT ================= */
export const sendBugReport = (message, email) =>
  API.post("/bug-report", { message, email });

/* ================= GLOBAL ERROR HANDLER ================= */
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Don't hard-redirect or wipe the session flag on a single 401: ask AuthProvider to re-check
      // the real session (/api/profile). ProtectedRoute redirects to /login only if it is really gone.
      localStorage.removeItem("token");
      window.dispatchEvent(new Event("subai:unauthorized"));
    }
    return Promise.reject(err);
  }
);

export default API;
